import { useEffect, useState } from 'react';

import { AVAILABLE_THEMES } from 'constants/constants';
import IUser from 'types/IUser';

// Extend the global Window interface to include Chatwoot properties
declare global {
  interface Window {
    chatwootSettings?: {
      hideMessageBubble: boolean;
      position: 'left' | 'right';
      locale: string;
      type: 'expanded_bubble' | 'standard';
      darkMode: 'light' | 'dark' | 'auto';
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setCustomAttributes: (attributes: Record<string, string>) => void;
      setUser: (
        id: string,
        userInfo: {
          email?: string;
          name?: string;
          phone_number?: string;
        }
      ) => void;
      reset: () => void;
      toggle: () => void;
    };
  }
}

const useChatwootWidget = ({
  user,
  isLiveChatEnabled,
}: {
  user: IUser | null;
  isLiveChatEnabled: boolean;
}): void => {
  const [chatwootReady, setChatwootReady] = useState(false);

  useEffect(() => {
    if (!isLiveChatEnabled) {
      return;
    }

    // Check if script is already injected
    const existingScript = document.getElementById('chatwoot-script');
    if (existingScript) {
      // If the script already exists, just mark Chatwoot as ready (assuming it already ran)
      setChatwootReady(true);
      return;
    }

    const BASE_URL = process.env.REACT_APP_CHATWOOT_BASE_URL;
    const WEBSITE_TOKEN = process.env.REACT_APP_CHATWOOT_WEBSITE_TOKEN;

    // Determine theme
    const isDarkMode =
      document.documentElement.classList.contains(AVAILABLE_THEMES.INDIAN_DARK) ||
      document.documentElement.classList.contains(AVAILABLE_THEMES.DARK);

    // Set chatwootSettings before loading the script
    window.chatwootSettings = {
      hideMessageBubble: true,
      position: 'right',
      locale: 'en',
      type: 'expanded_bubble',
      darkMode: isDarkMode ? 'dark' : 'light',
    };

    const script = document.createElement('script');
    script.id = 'chatwoot-script';
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.defer = true;

    // Once script is loaded, run Chatwoot
    script.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: WEBSITE_TOKEN || '',
          baseUrl: BASE_URL || '',
        });
        setChatwootReady(true);
        // eslint-disable-next-line no-console
        console.log('Chatwoot SDK loaded successfully');
      } else {
        console.error('Chatwoot SDK not loaded properly');
      }
    };

    script.onerror = () => {
      console.error('Failed to load Chatwoot script');
    };

    document.body.appendChild(script);
  }, [isLiveChatEnabled]);

  useEffect(() => {
    if (!chatwootReady || !user || !window.$chatwoot) return;

    window.$chatwoot.setCustomAttributes({
      auth_token: user.token || 'No auth token',
    });

    window.$chatwoot.setUser(user.id, {
      email: user.email,
      name: user.firstName || user.userName || 'NA',
      phone_number: user.phone_number || 'NA',
    });

    // eslint-disable-next-line no-console
    console.log('Chatwoot user details and custom attributes set');
  }, [chatwootReady, user]);
};

export { useChatwootWidget };
